/* ==========================================================================
   FLOR DE LOS REYES — motion & interactions
   Smooth scrolling (Lenis), reveals, split-word headings, counters, parallax,
   magnetic buttons, marquee, timeline, sticky photo story, product rails,
   collection filters, accordions, header + full-screen menu.
   Everything switches off for "reduce motion" and inside the theme editor.
   ========================================================================== */
(() => {
  if (window.RX) return;
  const RX = (window.RX = {});
  const d = document;
  const html = d.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const design = !!(window.Shopify && window.Shopify.designMode);
  const fine = matchMedia('(hover: hover) and (pointer: fine)').matches;
  const still = reduce || design;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const lerp = (a, b, t) => a + (b - a) * t;
  const $$ = (sel, root = d) => Array.from(root.querySelectorAll(sel));
  const easeOut = (t) => 1 - Math.pow(1 - t, 4);
  const expoOut = (t) => (t >= 1 ? 1 : 1 - Math.pow(2, -10 * t));

  /* ---------- Smooth scroll ---------- */
  let lenis = null;
  if (!still && typeof window.Lenis === 'function') {
    lenis = new window.Lenis({ lerp: 0.085, smoothWheel: true, wheelMultiplier: 0.95, touchMultiplier: 1.3 });
  }
  RX.lenis = lenis;
  const scrollY = () => (lenis ? lenis.scroll : window.scrollY);

  RX.scrollTo = (target, opts = {}) => {
    const el = typeof target === 'string' ? d.querySelector(target) : target;
    if (!el) return;
    const offset = opts.offset ?? 110;
    if (lenis) {
      lenis.scrollTo(el, { offset: -offset, duration: opts.duration ?? 1.6, easing: easeOut, immediate: !!opts.immediate });
    } else {
      const y = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top: y, behavior: reduce || opts.immediate ? 'auto' : 'smooth' });
    }
  };
  RX.lock = (on) => {
    html.classList.toggle('rx-locked', on);
    if (lenis) on ? lenis.stop() : lenis.start();
  };

  /* ---------- One animation loop for everything scroll-linked ---------- */
  const tickers = new Set();
  let lastY = -1;
  let vel = 0;
  const frame = (t) => {
    if (lenis) lenis.raf(t);
    const y = scrollY();
    const moved = y !== lastY;
    vel = lenis ? lenis.velocity : lerp(vel, lastY < 0 ? 0 : y - lastY, 0.25);
    lastY = y;
    tickers.forEach((fn) => fn(t, moved, y));
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);

  /* ---------- Reveals ---------- */
  const show = (el) => el.classList.add('is-in');
  const io = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      show(e.target);
      io.unobserve(e.target);
    }),
    { rootMargin: '0px 0px -8% 0px', threshold: 0.06 }
  );

  /* Split a heading into words that rise one after another (keeps <em> and gold styling). */
  function split(el) {
    if (el.hasAttribute('data-split')) return;
    // Chinese and Japanese have no spaces: split those into characters instead of one long "word".
    const cjk = /[　-鿿＀-￯]/;
    let i = 0;
    const walk = (node) => {
      Array.from(node.childNodes).forEach((n) => {
        if (n.nodeType === 3) {
          const frag = d.createDocumentFragment();
          let parts = cjk.test(n.textContent) ? n.textContent.split(/(\s+|)/u) : n.textContent.split(/(\s+)/);
          // Keep closing punctuation with the character before it, so "。" never starts a line on its own.
          parts = parts.filter(Boolean).reduce((acc, p) => {
            if (acc.length && /^[，。、！？：；」』）》〉,.!?:;)\]]$/.test(p) && !/^\s+$/.test(acc[acc.length - 1])) acc[acc.length - 1] += p;
            else acc.push(p);
            return acc;
          }, []);
          parts.forEach((part) => {
            if (!part) return;
            if (/^\s+$/.test(part)) { frag.appendChild(d.createTextNode(' ')); return; }
            const w = d.createElement('span');
            w.className = 'rx-w';
            const wi = d.createElement('span');
            wi.className = 'rx-wi';
            wi.textContent = part;
            wi.style.setProperty('--i', i++);
            w.appendChild(wi);
            frag.appendChild(w);
          });
          n.replaceWith(frag);
        } else if (n.nodeType === 1 && n.tagName !== 'BR') {
          walk(n);
        }
      });
    };
    walk(el);
    el.setAttribute('data-split', '');
  }

  /* ---------- Counters ---------- */
  function prepCount(el) {
    if (el._count) return;
    const raw = el.textContent.trim();
    // Only plain figures count up ("1920", "3", "100%"); anything else stays as written.
    const m = raw.match(/^([^\d\s]{0,3})(\d+)([^\d]{0,4})$/);
    if (!m) return;
    const num = parseInt(m[2], 10);
    // Years count up from a century earlier so they read as years, not as a race from zero.
    const from = num >= 1000 && num <= 2100 ? num - 100 : 0;
    el._count = { pre: m[1], num, from, post: m[3] };
    if (still) return;
    el.textContent = m[1] + from + m[3];
    countIO.observe(el);
  }
  function runCount(el) {
    const c = el._count;
    const dur = c.num - c.from < 10 ? 1200 : 2200;
    const t0 = performance.now();
    const step = (t) => {
      const p = clamp((t - t0) / dur, 0, 1);
      el.textContent = c.pre + Math.round(lerp(c.from, c.num, expoOut(p))) + c.post;
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }
  const countIO = new IntersectionObserver(
    (entries) => entries.forEach((e) => {
      if (!e.isIntersecting) return;
      countIO.unobserve(e.target);
      setTimeout(() => runCount(e.target), 200);
    }),
    { threshold: 0.6 }
  );

  /* ---------- Parallax ---------- */
  const pars = [];
  let parForce = true;
  function addParallax(el) {
    if (still || el._par) return;
    el._par = true;
    pars.push({ el, f: parseFloat(el.dataset.rxParallax) || 0.1, box: el.parentElement });
  }
  tickers.add((t, moved) => {
    if (!pars.length || (!moved && !parForce)) return;
    parForce = false;
    const vh = innerHeight;
    for (const p of pars) {
      const r = p.box.getBoundingClientRect();
      if (r.bottom < -200 || r.top > vh + 200) continue;
      const prog = (r.top + r.height / 2 - vh / 2) / vh;
      p.el.style.transform = `translate3d(0, ${(prog * p.f * 100).toFixed(3)}%, 0)`;
    }
  });
  addEventListener('resize', () => { parForce = true; }, { passive: true });

  /* ---------- Magnetic buttons ---------- */
  function magnet(el) {
    if (!fine || still || el._mag) return;
    el._mag = true;
    const s = parseFloat(el.dataset.rxMagnet) || 0.28;
    el.addEventListener('pointermove', (e) => {
      const r = el.getBoundingClientRect();
      const x = e.clientX - r.left - r.width / 2;
      const y = e.clientY - r.top - r.height / 2;
      el.style.transition = 'transform .35s cubic-bezier(.16,1,.3,1), color .6s, border-color .6s';
      el.style.transform = `translate3d(${(x * s).toFixed(1)}px, ${(y * s).toFixed(1)}px, 0)`;
    });
    el.addEventListener('pointerleave', () => { el.style.transition = ''; el.style.transform = ''; });
  }

  /* ---------- Marquee (speeds up and leans with the scroll) ---------- */
  function marquee(root) {
    if (root._mq) return;
    root._mq = true;
    const track = root.querySelector('.rx-marquee__track');
    const group = track && track.querySelector('.rx-marquee__group');
    if (!group) return;
    // Enough copies to cover wide screens without gaps.
    for (let k = 0; k < 3; k++) {
      const clone = group.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
    }
    if (reduce) return;
    let w = group.offsetWidth, x = 0, skew = 0, dirS = 1, visible = false, last = performance.now();
    const speed = parseFloat(root.dataset.speed) || 45;
    new ResizeObserver(() => { w = group.offsetWidth; }).observe(group);
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; }).observe(root);
    tickers.add((t) => {
      const dt = Math.min(64, t - last) / 1000;
      last = t;
      if (!visible || !w) return;
      dirS = lerp(dirS, vel < -0.4 ? -1 : 1, 0.06);
      x -= dirS * speed * (1 + Math.min(Math.abs(vel) * 0.14, 7)) * dt;
      if (x <= -w) x += w;
      if (x > 0) x -= w;
      skew = lerp(skew, clamp(-vel * 0.3, -6, 6), 0.12);
      track.style.transform = `translate3d(${x.toFixed(2)}px, 0, 0) skewX(${skew.toFixed(2)}deg)`;
    });
  }

  /* ---------- Timeline that fills with gold as you scroll ---------- */
  function timeline(el) {
    if (el._tl) return;
    el._tl = true;
    const steps = $$('.rx-chapter', el);
    if (still) { el.style.setProperty('--p', 1); steps.forEach((s) => s.classList.add('is-lit')); return; }
    let prev = -1;
    tickers.add((t, moved) => {
      if (!moved && prev >= 0) return;
      const r = el.getBoundingClientRect();
      const vh = innerHeight;
      if (r.bottom < -50 || r.top > vh + 50) { if (prev < 0) prev = 0; return; }
      const p = clamp((vh * 0.6 - r.top) / r.height, 0, 1);
      if (Math.abs(p - prev) < 0.0005) return;
      prev = p;
      el.style.setProperty('--p', p.toFixed(4));
      steps.forEach((s) => s.classList.toggle('is-lit', p > 0.001 && p >= (s.offsetTop + 50) / Math.max(1, r.height) - 0.02));
    });
  }

  /* ---------- Sticky photo story: the photo follows the step being read ---------- */
  function story(root) {
    if (root._story) return;
    root._story = true;
    const steps = $$('.rx-step', root);
    const pics = $$('.rx-story__pic', root);
    if (!steps.length) return;
    let cur = -1;
    const set = (i) => {
      if (i === cur) return;
      cur = i;
      steps.forEach((s, k) => s.classList.toggle('is-active', k === i));
      pics.forEach((p, k) => {
        p.classList.toggle('is-active', k === i);
        p.classList.toggle('is-past', k < i);
      });
    };
    set(0);
    tickers.add((t, moved) => {
      if (!moved && cur >= 0) return;
      const mid = innerHeight * 0.55;
      let best = 0, dist = Infinity;
      steps.forEach((s, k) => {
        const r = s.getBoundingClientRect();
        const dd = Math.abs(r.top + r.height / 2 - mid);
        if (dd < dist) { dist = dd; best = k; }
      });
      set(best);
    });
  }

  /* ---------- Product rail: arrows, progress line and drag to scroll ---------- */
  function rail(root) {
    if (root._rail) return;
    root._rail = true;
    const track = root.querySelector('.rx-rail__track');
    if (!track) return;
    const bar = root.querySelector('.rx-rail__progress');
    const prev = root.querySelector('[data-rail-prev]');
    const next = root.querySelector('[data-rail-next]');
    const step = () => {
      const first = track.firstElementChild;
      return first ? first.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 0) : track.clientWidth * 0.8;
    };
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const vis = track.clientWidth / Math.max(1, track.scrollWidth);
      const p = max > 0 ? track.scrollLeft / max : 1;
      if (bar) bar.style.setProperty('--p', Math.max(vis, vis + (1 - vis) * p).toFixed(4));
      if (prev) prev.disabled = track.scrollLeft < 4;
      if (next) next.disabled = track.scrollLeft > max - 4;
    };
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduce ? 'auto' : 'smooth' }));
    if (next) next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduce ? 'auto' : 'smooth' }));
    // Drag with the mouse (touch already swipes natively).
    if (fine) {
      let down = false, sx = 0, sl = 0, movedPx = 0;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0 || e.target.closest('button, input, select, form')) return;
        down = true; sx = e.clientX; sl = track.scrollLeft; movedPx = 0;
      });
      addEventListener('pointermove', (e) => {
        if (!down) return;
        const dx = e.clientX - sx;
        movedPx = Math.max(movedPx, Math.abs(dx));
        if (movedPx > 6) { track.classList.add('is-dragging'); track.scrollLeft = sl - dx; }
      });
      addEventListener('pointerup', () => {
        if (!down) return;
        down = false;
        if (track.classList.contains('is-dragging')) {
          // Let it settle on the nearest card again.
          requestAnimationFrame(() => track.classList.remove('is-dragging'));
        }
      });
      track.addEventListener('click', (e) => { if (movedPx > 6) { e.preventDefault(); e.stopPropagation(); movedPx = 0; } }, true);
      track.addEventListener('dragstart', (e) => e.preventDefault());
    }
    update();
  }

  /* ---------- Collection filters with a sliding gold pill ---------- */
  function filters(root) {
    if (root._flt) return;
    root._flt = true;
    const bar = root.querySelector('.rx-filters');
    const chips = $$('[data-fdlr-filter]', root);
    const cards = $$('[data-kind]', root);
    const count = root.querySelector('[data-fdlr-count]');
    if (!bar || !chips.length) return;
    const pill = d.createElement('span');
    pill.className = 'rx-filters__pill';
    pill.setAttribute('aria-hidden', 'true');
    bar.prepend(pill);
    const place = (chip, instant) => {
      if (instant) pill.style.transition = 'none';
      pill.style.width = chip.offsetWidth + 'px';
      pill.style.height = chip.offsetHeight + 'px';
      pill.style.transform = `translate3d(${chip.offsetLeft}px, ${chip.offsetTop}px, 0)`;
      if (instant) { void pill.offsetWidth; pill.style.transition = ''; }
    };
    const apply = (chip) => {
      const f = chip.getAttribute('data-fdlr-filter');
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      place(chip);
      let shown = 0;
      cards.forEach((card) => {
        const on = f === 'all' ||
          (f.indexOf('kind:') === 0 && card.getAttribute('data-kind') === f.slice(5)) ||
          (f.indexOf('len:') === 0 && card.getAttribute('data-length') === f.slice(4));
        if (on) {
          shown++;
          card.hidden = false;
          show(card);
          requestAnimationFrame(() => requestAnimationFrame(() => card.classList.remove('is-out')));
        } else {
          card.classList.add('is-out');
          setTimeout(() => { if (card.classList.contains('is-out')) card.hidden = true; if (lenis) lenis.resize(); parForce = true; }, reduce ? 0 : 380);
        }
      });
      if (count) count.textContent = (shown === 1 ? count.dataset.one : count.dataset.other).replace('%n', shown);
    };
    chips.forEach((c) => c.addEventListener('click', () => apply(c)));
    const start = () => place(chips.find((c) => c.getAttribute('aria-pressed') === 'true') || chips[0], true);
    start();
    new ResizeObserver(start).observe(bar);
  }

  /* ---------- Accordions (product details, FAQ) ---------- */
  function accordion(root) {
    if (root._acc) return;
    root._acc = true;
    $$('.rx-acc__btn', root).forEach((b) => b.addEventListener('click', () => {
      const item = b.closest('.rx-acc__item');
      const open = !item.classList.contains('is-open');
      if (root.hasAttribute('data-rx-acc-single')) $$('.rx-acc__item.is-open', root).forEach((o) => { if (o !== item) { o.classList.remove('is-open'); o.querySelector('.rx-acc__btn').setAttribute('aria-expanded', 'false'); } });
      item.classList.toggle('is-open', open);
      b.setAttribute('aria-expanded', String(open));
      if (lenis) setTimeout(() => lenis.resize(), 850);
      parForce = true;
    }));
  }

  /* ---------- Header: melts into a floating band; full-screen menu on phones ---------- */
  function header() {
    const h = d.querySelector('[data-rxh]');
    if (!h) return;
    const set = (y) => {
      const on = y > 30;
      h.classList.toggle('is-scrolled', on);
      html.classList.toggle('rx-scrolled', on);
    };
    tickers.add((t, moved, y) => { if (moved) set(y); });
    set(window.scrollY);
    const btn = h.querySelector('[data-rxh-burger]');
    const menu = d.getElementById('rxh-menu');
    if (!btn || !menu) return;
    const open = (on) => {
      btn.setAttribute('aria-expanded', String(on));
      menu.classList.toggle('is-open', on);
      menu.toggleAttribute('inert', !on);
      h.classList.toggle('menu-open', on);
      RX.lock(on);
    };
    menu.toggleAttribute('inert', true);
    btn.addEventListener('click', () => open(btn.getAttribute('aria-expanded') !== 'true'));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) open(false); });
    d.addEventListener('keydown', (e) => { if (e.key === 'Escape' && menu.classList.contains('is-open')) { open(false); btn.focus(); } });
    matchMedia('(min-width: 961px)').addEventListener('change', (e) => { if (e.matches && menu.classList.contains('is-open')) open(false); });
  }

  /* Same-page links glide instead of jumping. */
  d.addEventListener('click', (e) => {
    const a = e.target.closest('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || a.target === '_blank') return;
    let url;
    try { url = new URL(a.getAttribute('href'), location.href); } catch (_) { return; }
    if (url.origin !== location.origin || url.pathname !== location.pathname || url.search !== location.search || !url.hash || url.hash === '#') return;
    const target = d.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    e.preventDefault();
    RX.scrollTo(target);
    history.replaceState(null, '', url.hash);
  });

  /* ---------- Hero image eases in once it has loaded ---------- */
  function heroLoad(hero) {
    const img = hero.querySelector('img');
    const done = () => requestAnimationFrame(() => hero.classList.add('is-loaded'));
    if (!img || img.complete) done();
    else { img.addEventListener('load', done, { once: true }); img.addEventListener('error', done, { once: true }); setTimeout(done, 2500); }
  }

  /* ---------- Boot ---------- */
  function interactive(scope) {
    $$('[data-rx-marquee]', scope).forEach(marquee);
    $$('[data-rx-magnet]', scope).forEach(magnet);
    $$('[data-rx-rail]', scope).forEach(rail);
    $$('[data-rx-filters]', scope).forEach(filters);
    $$('[data-rx-acc]', scope).forEach(accordion);
  }
  function motion(scope) {
    $$('[data-rx-split]', scope).forEach(split);
    $$('[data-rx-count]', scope).forEach(prepCount);
    $$('[data-rx], [data-split]', scope).forEach((el) => (still ? show(el) : io.observe(el)));
    $$('[data-rx-parallax]', scope).forEach(addParallax);
    $$('[data-rx-tl]', scope).forEach(timeline);
    $$('[data-rx-story]', scope).forEach(story);
    $$('[data-rx-hero]', scope).forEach(heroLoad);
    parForce = true;
  }

  header();
  interactive(d);
  const fontsReady = d.fonts && d.fonts.ready ? Promise.race([d.fonts.ready, new Promise((r) => setTimeout(r, 700))]) : Promise.resolve();
  fontsReady.then(() => {
    motion(d);
    html.classList.add('rx-ready');
    if (lenis) setTimeout(() => lenis.resize(), 400);
  });

  // The age check (if showing) holds the page still until it is answered.
  const gate = d.getElementById('fdlr-age');
  if (gate && !gate.hidden) RX.lock(true);
  d.addEventListener('rx:unlock', () => RX.lock(false));

  d.addEventListener('shopify:section:load', (e) => { interactive(e.target); motion(e.target); });
  addEventListener('load', () => { parForce = true; if (lenis) lenis.resize(); });
  RX.refresh = () => { parForce = true; if (lenis) lenis.resize(); };
  RX.show = show;
})();
